import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatHeat } from "@/utils/format";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "wide" | "compact";
}

export default function ArticleCard({ article, variant = "default" }: ArticleCardProps) {
  const to = `/article/${article.id}`;

  if (variant === "compact") {
    return (
      <Link
        to={to}
        className="group flex cursor-pointer items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
      >
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-700">
          <i className="ri-fire-line text-base" aria-hidden="true"></i>
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
            {article.title}
          </span>
          <span className="mt-1 flex items-center gap-2 font-label text-xs text-foreground-500">
            <span>{article.categoryName}</span>
            <span aria-hidden="true">·</span>
            <span>{formatHeat(article.heat)} 热度</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={to}
        className="group flex cursor-pointer flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:flex-row"
      >
        <div className="relative h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-100 md:h-32 md:w-56">
          <img
            src={article.cover}
            alt={`${article.title} 话题配图`}
            title={`${article.title} 相关热点`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col">
          <span className="mb-2 inline-flex w-fit items-center rounded-full bg-secondary-100 px-2.5 py-0.5 font-label text-xs text-secondary-800">
            {article.categoryName}
          </span>
          <h3 className="text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
          <span className="mt-3 flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <span>{article.author}</span>
            <span aria-hidden="true">·</span>
            <span>{article.publishedAt}</span>
            {article.live && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-background-50">
                <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-background-50" />
                实时
              </span>
            )}
          </span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-background-100">
        <img
          src={article.cover}
          alt={`${article.title} 话题配图`}
          title={`${article.title} 相关热点`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-foreground-950/75 px-2 py-1 font-label text-xs text-background-50 backdrop-blur-sm">
          {article.categoryName}
        </span>
        {article.live && (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md bg-primary-500 px-2 py-1 font-label text-xs text-background-50">
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-background-50" />
            实时楼
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">{article.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
          <span>{article.author}</span>
          <span aria-hidden="true">·</span>
          <span>{article.readMinutes} 分钟阅读</span>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1 text-primary-700">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {formatHeat(article.heat)}
          </span>
        </div>
      </div>
    </Link>
  );
}